import React, { useState } from 'react';
import { Debt, DebtInput, DebtType, DEBT_TYPE_LABEL } from '../services/api';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { abrirSeletorDeData } from '../utils/abrirSeletorDeData';
import { mascara, numero } from './ValorDoMes';
import { LIMITES } from '../utils/limites';
import './ValorDoMes.css';
import './QuitarDivida.css';

/**
 * Corrigir os dados de uma dívida (27/10).
 *
 * A dívida nasce de uma frase interpretada pela IA, e até aqui o card só
 * trocava "sai do salário / pago por fora": se a IA errou a taxa ou a data, a
 * saída era apagar e recriar. Aqui ela corrige o cadastro.
 *
 * Corrigir não é pagar. Antecipar parcela é a "Quitar", que guarda o
 * histórico e a economia; mexer no total de parcelas por aqui reescreve o
 * contrato sem deixar rastro. O texto do topo diz isso.
 *
 * Só vai para o servidor o que mudou: `PUT /debts/:id` preserva o resto.
 * Mesma janela da quitação (`vdm-janela`), aberta só pelo clique dela.
 */

/** O que ela pode corrigir — a forma do `PUT`. */
export type MudancasDaDivida = Partial<Pick<DebtInput,
  'name' | 'type' | 'installments' | 'installmentValue' | 'firstInstallmentDate'
  | 'deductedFromSalary' | 'monthlyRate'>>;

/**
 * A taxa como ela digita, em % ao mês: "2,3" → 2.3. Nunca dividida por 100 —
 * o banco guarda em %. Vazio é `null` (não informada); inválido é `NaN`.
 */
export const lerTaxa = (texto: string): number | null => {
  const t = texto.trim().replace('%', '').trim();
  if (t === '') return null;
  const v = Number(t.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(v) && v >= 0 ? v : NaN;
};

/** A taxa guardada, escrita como ela leria: 2.3 → "2,3". */
export const taxaComoTexto = (v: number | null | undefined): string =>
  v == null ? '' : String(v).replace('.', ',');

/** Um valor em reais na máscara do app: 1950 → "1.950,00". */
export const valorComoMascara = (v: number | null | undefined): string =>
  v == null || v <= 0 ? '' : mascara(String(Math.round(v * 100)));

const TIPOS: DebtType[] = ['consignado', 'pessoal', 'financiamento', 'cartao', 'cheque-especial', 'outro'];

interface Props {
  debt: Debt;
  onSalvar: (mudancas: MudancasDaDivida) => Promise<void>;
  onFechar: () => void;
}

const EditarDivida: React.FC<Props> = ({ debt, onSalvar, onFechar }) => {
  const dataAtual = (debt.firstInstallmentDate ?? '').slice(0, 10);
  const [nome, setNome] = useState(debt.name);
  const [tipo, setTipo] = useState<DebtType>(debt.type);
  const [parcelas, setParcelas] = useState(String(debt.installments));
  const [valor, setValor] = useState(valorComoMascara(debt.installmentValue));
  const [data, setData] = useState(dataAtual);
  const [taxa, setTaxa] = useState(taxaComoTexto(debt.monthlyRate));
  const [folha, setFolha] = useState(debt.deductedFromSalary);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');

  const nParcelas = Number(parcelas);
  const nValor = valor === '' ? NaN : numero(valor);
  const nTaxa = lerTaxa(taxa);

  const problema =
    !nome.trim() ? 'Dê um nome para a dívida.'
      : !Number.isInteger(nParcelas) || nParcelas < 1 ? 'O total de parcelas precisa ser 1 ou mais.'
        : !(nValor > 0) ? 'Informe o valor da parcela.'
          : !/^\d{4}-\d{2}-\d{2}$/.test(data) ? 'Informe a data da primeira parcela.'
            : Number.isNaN(nTaxa) ? 'A taxa é um número em % ao mês, como 2,3.'
              : null;

  // Só o que mudou. Taxa vazia não entra: o servidor não apaga por esta porta.
  const mudancas: MudancasDaDivida = {};
  if (!problema) {
    if (nome.trim() !== debt.name) mudancas.name = nome.trim();
    if (tipo !== debt.type) mudancas.type = tipo;
    if (nParcelas !== debt.installments) mudancas.installments = nParcelas;
    if (Math.abs(nValor - debt.installmentValue) >= 0.005) mudancas.installmentValue = nValor;
    if (data !== dataAtual) mudancas.firstInstallmentDate = data;
    if (folha !== debt.deductedFromSalary) mudancas.deductedFromSalary = folha;
    if (nTaxa != null && nTaxa !== debt.monthlyRate) mudancas.monthlyRate = nTaxa;
  }
  const mudou = Object.keys(mudancas).length > 0;

  const salvar = async () => {
    if (problema || !mudou || ocupado) return;
    setOcupado(true);
    setErro('');
    try {
      await onSalvar(mudancas);
      onFechar();
    } catch (e) {
      // Falhou: a janela fica aberta e DIZ — fechar calada faria parecer gravado.
      setErro(mensagemDeErro(e, 'Não consegui salvar a dívida. Tente de novo.'));
      setOcupado(false);
    }
  };

  return (
    <div className="vdm-overlay" onClick={onFechar}>
      <div
        className="vdm-janela qtd-janela edt-janela"
        role="dialog"
        aria-modal="true"
        aria-label={`Editar ${debt.name}`}
        onClick={e => e.stopPropagation()}
      >
        <h3 className="vdm-titulo">Editar {debt.name}</h3>
        <p className="vdm-texto">
          Aqui você corrige um dado errado. Se pagou parcelas adiantado, use <strong>Quitar</strong>:
          ela guarda o histórico e quanto você economizou.
        </p>

        <div className="qtd-campo">
          <label htmlFor="edt-nome">Nome</label>
          <input id="edt-nome" type="text" value={nome} maxLength={LIMITES.nome} onChange={e => setNome(e.target.value)} />
        </div>

        <div className="qtd-campo">
          <label htmlFor="edt-tipo">Tipo</label>
          <select id="edt-tipo" value={tipo} onChange={e => setTipo(e.target.value as DebtType)}>
            {TIPOS.map(t => <option key={t} value={t}>{DEBT_TYPE_LABEL[t]}</option>)}
          </select>
        </div>

        <div className="qtd-campo">
          <label htmlFor="edt-parcelas">Total de parcelas do contrato</label>
          <input
            id="edt-parcelas" type="number" inputMode="numeric" min={1}
            value={parcelas} onChange={e => setParcelas(e.target.value.replace(/\D/g, ''))}
          />
        </div>

        <div className="qtd-campo">
          <label htmlFor="edt-valor">Valor da parcela</label>
          <div className="vdm-outro-linha">
            <span className="vdm-rs">R$</span>
            <input
              id="edt-valor" type="text" inputMode="numeric" placeholder="0,00"
              value={valor} onChange={e => setValor(mascara(e.target.value))}
            />
          </div>
        </div>

        <div className="qtd-campo">
          <label htmlFor="edt-data">Data da primeira parcela</label>
          <input
            id="edt-data" type="date" onClick={abrirSeletorDeData}
            value={data} onChange={e => setData(e.target.value)}
          />
        </div>

        <div className="qtd-campo">
          <label htmlFor="edt-taxa">Taxa (% ao mês)</label>
          <input
            id="edt-taxa" type="text" inputMode="decimal" placeholder="ex: 2,3"
            value={taxa} onChange={e => setTaxa(e.target.value)}
          />
          {debt.monthlyRate != null && taxa.trim() === '' && (
            <p className="qtd-nota">Em branco, a taxa de {taxaComoTexto(debt.monthlyRate)}% continua.</p>
          )}
        </div>

        <fieldset className="qtd-campo">
          <legend>Como a parcela é paga</legend>
          <div className="qtd-opcoes">
            <label className={`qtd-opcao${folha ? ' ativa' : ''}`}>
              <input type="radio" name="edt-folha" checked={folha} onChange={() => setFolha(true)} />
              Descontada em folha
            </label>
            <label className={`qtd-opcao${!folha ? ' ativa' : ''}`}>
              <input type="radio" name="edt-folha" checked={!folha} onChange={() => setFolha(false)} />
              Eu pago por fora
            </label>
          </div>
        </fieldset>

        {problema && <p className="vdm-erro" role="alert">{problema}</p>}
        {erro && <p className="vdm-erro" role="alert">{erro}</p>}
        <button
          type="button" className="vdm-btn vdm-btn-principal"
          disabled={!!problema || !mudou || ocupado} aria-busy={ocupado} onClick={salvar}
        >
          {mudou || problema ? 'Salvar' : 'Nada mudou'}
        </button>
        <button type="button" className="vdm-cancelar" onClick={onFechar} disabled={ocupado}>Cancelar</button>
      </div>
    </div>
  );
};

export default EditarDivida;
